import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, MenuItem, FormControl, Select } from '@mui/material';

const ProduccionPorReferencia = () => {
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [referencia, setReferencia] = useState('');
  const [referencias, setReferencias] = useState([]);

  useEffect(() => {
    // Cargar referencias al montar
    const fetchReferencias = async () => {
      try {
        const res = await fetch('/api/referencias');
        const data = await res.json();
        setReferencias(data.data || data);
      } catch (err) {
        // No mostrar error aquí, solo dejar vacío
      }
    };
    fetchReferencias();

    // Cargar datos de la tabla desde localStorage si existen
    try {
      const guardado = localStorage.getItem('produccionPorReferencia');
      if (guardado) {
        setData(JSON.parse(guardado));
      }
    } catch (e) {
      // Ignorar errores de parseo
    }
  }, []);

  // Función para formatear números sin decimales y con separador de miles
  const formatearNumero = (valor) => {
    const num = Number(valor) || 0;
    return Math.round(num).toLocaleString('es-ES');
  };

  const handleBuscar = async () => {
    setLoading(true);
    setError('');
    setData([]);
    
    // Limpiar caché al buscar nuevos datos
    try {
      localStorage.removeItem('produccionPorReferencia');
    } catch (e) {
      // Ignorar errores
    }
    
    try {
      let url = `/api/produccion-referencia?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`;
      if (referencia) url += `&referencia=${encodeURIComponent(referencia)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Error al consultar producción');
      const result = await res.json();
      const tabla = result && result.data !== undefined ? result.data : result;
      setData(tabla);
      
      // Guardar en localStorage
      try {
        localStorage.setItem('produccionPorReferencia', JSON.stringify(tabla));
      } catch (e) {
        // Ignorar errores de almacenamiento
      }
    } catch (err) {
      setError('No se pudo obtener la producción.');
    } finally {
      setLoading(false);
    }
  };

  // Agrupar por referencia
  const agrupado = {};
  data.forEach(row => {
    const ref = row.referencia || 'Sin referencia';
    if (!agrupado[ref]) agrupado[ref] = [];
    agrupado[ref].push(row);
  });

  return (
    <Box sx={{ maxWidth: 1100, margin: '40px auto', p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Producción diaria por referencia
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Fecha inicio"
          type="date"
          value={fechaInicio}
          onChange={e => setFechaInicio(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: '#302f2fff'
            }
          }}
        />
        <TextField
          label="Fecha fin"
          type="date"
          value={fechaFin}
          onChange={e => setFechaFin(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: '#302f2fff'
            }
          }}
        />
        <FormControl sx={{ minWidth: 180 }}>
          <Select
            value={referencia}
            onChange={e => setReferencia(e.target.value)}
            displayEmpty
            renderValue={(selected) =>
              selected === '' ? <em>Todas las referencias</em> : selected
            }
          >
            <MenuItem value=""><em>Todas las referencias</em></MenuItem>
            {referencias.map(r => (
              <MenuItem key={r._id} value={r.referencia}>{r.referencia}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleBuscar} disabled={loading || !fechaInicio || !fechaFin}>
          Buscar
        </Button>
      </Box>
      {loading && <CircularProgress />}
      {error && <Typography color="error">{error}</Typography>}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell>Referencia</TableCell>
              <TableCell>Máquina</TableCell>
              <TableCell>Revoluciones</TableCell>
              <TableCell>Rev/Metro</TableCell>
              <TableCell>Producción (m)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">Sin datos</TableCell>
              </TableRow>
            ) : (
              Object.entries(agrupado).map(([ref, rows], gidx) => [
                <TableRow key={ref + '-header'} sx={{ backgroundColor: '#111' }}>
                  <TableCell colSpan={6} style={{ fontWeight: 'bold', color: '#777', backgroundColor: '#111' }}>{ref}</TableCell>
                </TableRow>,
                ...rows.map((row, idx) => (
                  <TableRow key={gidx + '-' + idx}>
                    <TableCell>{row.fecha}</TableCell>
                    <TableCell>{row.referencia}</TableCell>
                    <TableCell>{row.maquina || row.machineNumber || ''}</TableCell>
                    <TableCell>{formatearNumero(row.revoluciones)}</TableCell>
                    <TableCell>{formatearNumero(row.revolucionesPorMetro || row.rendimiento)}</TableCell>
                    <TableCell>{formatearNumero(row.produccion)}</TableCell>
                  </TableRow>
                )),
                <TableRow key={ref + '-subtotal'} sx={{ backgroundColor: '#111' }}>
                  <TableCell colSpan={5} align="right" style={{ fontWeight: 'bold', color: '#777', backgroundColor: '#111' }}>Subtotal producción (metros):</TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: '#777', backgroundColor: '#111' }}>
                    {formatearNumero(rows.reduce((sum, r) => sum + (Number(r.produccion) || 0), 0))}
                  </TableCell>
                </TableRow>
              ])
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProduccionPorReferencia;
